/**
 * The order panel: what is wrong with this order, where its time went, and what was asked for.
 *
 * Everything on it is this order's and nothing else's. The problems are the ones the list
 * flagged, spelled out with their figures, each one click from the moment it happened — the
 * list says *late*, this says *six minutes late, here*. The bar is the order's own time, part
 * by part, in the timeline's colours; a part is a click from its start. And the request is
 * what the car was asked to be and do, which a complaint is so often about.
 */

import { useTranslation } from 'react-i18next';

import { useTenantFormats } from '../shell/formats';
import { Button, Card, CardHeader, Icon, Pill, cx } from '../ui';
import { useFeatureName } from '../vehicles/featureNames';
import { className as vehicleClassName } from '../vehicles/words';
import {
  chapterSpans,
  distanceWords,
  problemItems,
  problemTone,
  type JourneyDetail,
  type ProblemItem,
  sourceWord,
} from './journey';

const PART_COLOUR: Record<string, string> = {
  call: 'bg-ink-3',
  searching: 'bg-ink-3',
  approach: 'bg-info',
  waiting: 'bg-warn',
  aboard: 'bg-ok',
};

export function JourneyOrder({
  journey,
  onSeek,
  className,
}: {
  journey: JourneyDetail;
  /** Jump the replay to a real moment. */
  onSeek: (moment: number) => void;
  className?: string;
}) {
  const { t } = useTranslation();

  return (
    <Card className={cx('flex flex-col', className)}>
      <CardHeader title={t('reports.journeys.order.title')} />
      <div className="flex flex-col gap-4 px-4 py-3 min-h-0 overflow-y-auto">
        <Problems journey={journey} onSeek={onSeek} />
        <TimeByPart journey={journey} onSeek={onSeek} />
        <Request journey={journey} />
      </div>
    </Card>
  );
}

function Heading({ children }: { children: string }) {
  return (
    <div className="text-caps font-semibold tracking-[0.06em] uppercase text-ink-3 mb-1.5">
      {children}
    </div>
  );
}

function Problems({
  journey,
  onSeek,
}: {
  journey: JourneyDetail;
  onSeek: (moment: number) => void;
}) {
  const { t } = useTranslation();
  const items = problemItems(journey);

  return (
    <section>
      <Heading>{t('reports.journeys.order.problems')}</Heading>

      {items.length === 0 ? (
        <p className="m-0 flex items-center gap-2 text-table text-ok">
          <Icon name="check" small />
          {t('reports.journeys.order.clean')}
        </p>
      ) : (
        <ul className="m-0 p-0 list-none flex flex-col gap-2">
          {items.map((item, index) => {
            const at = item.at;

            return (
              <li
                key={`${item.problem}-${at}-${index}`}
                className="flex items-start gap-2 text-table"
              >
                <Pill soft tone={problemTone(item.problem)}>
                  {t(`reports.journeys.problems.${item.problem}`)}
                </Pill>
                <span className="flex-1 min-w-0 pt-0.5">
                  <Explain item={item} />
                </span>
                {at !== null && (
                  <Button
                    size="sm"
                    title={t('reports.journeys.order.showHelp')}
                    onClick={() => onSeek(at)}
                  >
                    <Icon name="play" small />
                    {t('reports.journeys.order.show')}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** One problem, said with its figures. */
function Explain({ item }: { item: ProblemItem }) {
  const { t } = useTranslation();
  const formats = useTenantFormats();
  const time = item.at === null ? '' : formats.timeWithSeconds(new Date(item.at).toISOString());
  const duration = formats.duration(item.seconds ?? null);
  const near = (metres: number | undefined) =>
    metres == null ? '—' : distanceWords(t, formats, metres);

  switch (item.problem) {
    case 'late':
      return t('reports.journeys.order.explain.late', { time: duration });
    case 'detour':
      return t('reports.journeys.order.explain.detour', {
        driven: near(item.metres),
        best: near(item.recommendedMetres),
        percent: item.percent == null ? '—' : formats.count(Math.round(item.percent)),
      });
    case 'longWait':
      return t('reports.journeys.order.explain.longWait', { time: duration });
    case 'longStop':
      return t('reports.journeys.order.explain.longStop', { time: duration, at: time });
    case 'speeding':
      return t('reports.journeys.order.explain.speeding', {
        speed: item.kmh ?? '—',
        limit: item.limitKmh ?? '—',
        at: time,
      });
    case 'signalLost':
      return t('reports.journeys.order.explain.signalLost', { time: duration, at: time });
    case 'cancelledOnTheWay':
      return item.metres == null
        ? t('reports.journeys.order.explain.cancelledOnTheWay', { at: time })
        : t('reports.journeys.order.explain.cancelledAway', {
            at: time,
            distance: near(item.metres),
          });
    case 'noTrack':
      return t('reports.journeys.order.explain.noTrack');
    default:
      return null;
  }
}

/** Where the order's time went, part by part, as one bar. */
function TimeByPart({
  journey,
  onSeek,
}: {
  journey: JourneyDetail;
  onSeek: (moment: number) => void;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();
  const parts = chapterSpans(journey).filter((part) => part.seconds > 0);
  const total = parts.reduce((sum, part) => sum + part.seconds, 0);

  if (total === 0) return null;

  return (
    <section>
      <Heading>{t('reports.journeys.order.time')}</Heading>

      <div className="flex h-3 rounded-pill overflow-hidden bg-surface-2" aria-hidden="true">
        {parts.map((part) => (
          <button
            key={part.kind}
            type="button"
            tabIndex={-1}
            className={cx(
              'h-full cursor-pointer border-r border-surface last:border-r-0',
              PART_COLOUR[part.kind] ?? 'bg-ink-3',
            )}
            style={{ width: `${(part.seconds / total) * 100}%` }}
            title={t(`reports.journeys.chapter.${part.kind}`)}
            onClick={() => onSeek(part.from)}
          />
        ))}
      </div>

      <ul className="m-0 mt-2 p-0 list-none flex flex-col">
        {parts.map((part) => (
          <li key={part.kind}>
            <button
              type="button"
              className="w-full grid grid-cols-[10px_minmax(0,1fr)_auto_auto] items-center gap-2 py-1 text-left text-table cursor-pointer rounded-control hover:bg-surface-2"
              onClick={() => onSeek(part.from)}
            >
              <span
                className={cx('w-2.5 h-2.5 rounded-full', PART_COLOUR[part.kind] ?? 'bg-ink-3')}
              />
              <span className="min-w-0 truncate">{t(`reports.journeys.chapter.${part.kind}`)}</span>
              <span className="tabular-nums text-ink-3">
                {part.metres != null && part.metres > 0
                  ? distanceWords(t, formats, part.metres)
                  : ''}
              </span>
              <span className="tabular-nums font-semibold w-[72px] text-right">
                {formats.duration(part.seconds)}
              </span>
            </button>
          </li>
        ))}
        <li className="grid grid-cols-[10px_minmax(0,1fr)_auto] gap-2 pt-1 mt-1 border-t border-rule-soft text-table">
          <span />
          <span className="text-ink-3">{t('reports.journeys.order.total')}</span>
          <span className="tabular-nums font-semibold w-[72px] text-right">
            {formats.duration(total)}
          </span>
        </li>
      </ul>
    </section>
  );
}

/** What the order asked for: where, who, how many, what kind of car. The money is the fare card's. */
function Request({ journey }: { journey: JourneyDetail }) {
  const { t } = useTranslation();
  const formats = useTenantFormats();
  const featureNameOf = useFeatureName();

  const created = journey.events.find((e) => e.kind === 'created');
  const destination = journey.stops.length > 0 ? journey.stops[journey.stops.length - 1] : null;
  const between = journey.stops.slice(0, -1);
  const car = [
    journey.vehicleCategory ? vehicleClassName(t, journey.vehicleCategory) : null,
    ...journey.vehicleFeatures.map(featureNameOf),
  ].filter(Boolean);

  const rows: [string, string | null][] = [
    [
      t('reports.journeys.order.taken'),
      [
        formats.timeWithSeconds(journey.requestedAt),
        t(`reports.journeys.source.${sourceWord(journey.source)}`),
        created?.actorName ?? null,
      ]
        .filter(Boolean)
        .join(' · '),
    ],
    [
      t('reports.journeys.order.forWhen'),
      journey.scheduledFor ? formats.moment(journey.scheduledFor) : t('reports.journeys.order.now'),
    ],
    [t('reports.journeys.order.from'), journey.pickupAddress],
    [t('reports.journeys.order.notes'), journey.pickupNotes ?? null],
    [
      t('reports.journeys.order.via'),
      between.length > 0 ? between.map((s) => s.address).join(' → ') : null,
    ],
    [
      t('reports.journeys.order.to'),
      destination?.address ?? t('reports.journeys.order.noDestination'),
    ],
    [t('reports.journeys.order.passengers'), formats.count(journey.passengerCount)],
    [
      t('reports.journeys.order.car'),
      car.length > 0 ? car.join(' · ') : t('reports.journeys.order.anyCar'),
    ],
  ];

  return (
    <section>
      <Heading>{t('reports.journeys.order.asked')}</Heading>
      <dl className="grid grid-cols-[minmax(110px,auto)_1fr] gap-x-4 gap-y-1.5 m-0 text-table">
        {rows
          .filter(([, value]) => value !== null && value !== '')
          .map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="m-0 text-ink-3">{label}</dt>
              <dd className="m-0 min-w-0">{value}</dd>
            </div>
          ))}
      </dl>
    </section>
  );
}
